/** Types + helpers for Execution_COGS_Purchase_Dashboard.json (Purchase Analytical Dashboard) */

export type StringNumberMap = Record<string, number>;

export type PurchaseAnalyticalDashboardBlock = Partial<{
  "MoM Trend": StringNumberMap;
  "Vendor Wise Analysis": StringNumberMap;
  "Import/Domestic Analysis": StringNumberMap;
  "Product Type Analysis": StringNumberMap;
  "Product Rate Analysis (MoM)": Record<string, StringNumberMap>;
  "MoM Quantity vs Value (Total)": Record<string, { billing_quantity: number; invoice_amount: number }>;
  "MoM Quantity vs Value (Product)": Record<
    string,
    Record<string, { billing_quantity: number; invoice_amount: number }>
  >;
  "Plant Location Analysis": StringNumberMap;
  "Region Analysis": StringNumberMap;
}>;

export interface ComcPurchaseReportingPeriodPayload {
  "IPE Testing"?: {
    "Total Invoices": number;
    "Total Invoice Amount"?: number;
    "Total Billing Quantity"?: number;
  };
  "Purchase Analytical Dashboard"?: PurchaseAnalyticalDashboardBlock;
}

export type ComcPurchaseDashboardRoot = Record<string, ComcPurchaseReportingPeriodPayload>;

export function parseDdMmYyyy(key: string): number {
  const parts = key.trim().split(/[-/]/);
  if (parts.length !== 3) return 0;
  const d = Number(parts[0]);
  const m = Number(parts[1]) - 1;
  const y = Number(parts[2]);
  if (!y || m < 0 || m > 11) return 0;
  return new Date(y, m, d).getTime();
}

export function sortReportingDateKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => parseDdMmYyyy(a) - parseDdMmYyyy(b));
}

export function getPreviousReportingDate(sortedKeys: string[], selected: string): string | null {
  const i = sortedKeys.indexOf(selected);
  if (i <= 0) return null;
  return sortedKeys[i - 1] ?? null;
}

export function entriesSortedByValueDesc(m: StringNumberMap | undefined): [string, number][] {
  if (!m) return [];
  return Object.entries(m).sort((a, b) => b[1] - a[1]);
}

export function topNAndOthers(
  m: StringNumberMap | undefined,
  n: number,
): { top: [string, number][]; othersSum: number; othersLabel: string } {
  const entries = entriesSortedByValueDesc(m);
  const top = entries.slice(0, n);
  const othersSum = entries.slice(n).reduce((s, [, v]) => s + v, 0);
  return { top, othersSum, othersLabel: "Others" };
}

export interface RankBarRow {
  rank: number;
  value: number;
  entityName: string;
}

export function buildRankBarRows(m: StringNumberMap | undefined): RankBarRow[] {
  const entries = entriesSortedByValueDesc(m);
  return entries.map(([entityName, value], i) => ({
    rank: i + 1,
    value,
    entityName,
  }));
}

export function formatNum(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n);
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(rounded);
}

export function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n);
  return new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(rounded);
}

export function top10OverlapSummary(
  cy: StringNumberMap | undefined,
  py: StringNumberMap | undefined,
): { overlap: number; cyOnly: string[]; pyOnly: string[] } {
  const cyTop = new Set(topNAndOthers(cy, 10).top.map(([k]) => k));
  const pyTop = new Set(topNAndOthers(py, 10).top.map(([k]) => k));
  let overlap = 0;
  for (const k of cyTop) if (pyTop.has(k)) overlap++;
  const cyOnly = [...cyTop].filter((k) => !pyTop.has(k));
  const pyOnly = [...pyTop].filter((k) => !cyTop.has(k));
  return { overlap, cyOnly, pyOnly };
}

export function monthSortKey(month: string): number {
  const parts = month.split("-");
  if (parts.length < 2) return 0;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  return y * 12 + m;
}

export function sortMonthKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => monthSortKey(a) - monthSortKey(b));
}

export interface ProductMomRateRow {
  product: string;
  month: string;
  rate: number;
  variancePct: number | null;
  overTenPct: boolean;
}

export function buildProductRateMomTableRows(
  productRateMoM: Record<string, Record<string, number>> | undefined,
): ProductMomRateRow[] {
  if (!productRateMoM || typeof productRateMoM !== "object") return [];
  const out: ProductMomRateRow[] = [];
  for (const [product, monthMap] of Object.entries(productRateMoM)) {
    if (!monthMap || typeof monthMap !== "object") continue;
    const months = sortMonthKeys(Object.keys(monthMap));
    for (let i = 0; i < months.length; i++) {
      const month = months[i];
      const rate = Number(monthMap[month] ?? 0);
      let variancePct: number | null = null;
      let overTenPct = false;
      if (i > 0) {
        const prevRate = Number(monthMap[months[i - 1]] ?? 0);
        if (Math.abs(prevRate) > 1e-12) {
          variancePct = ((rate - prevRate) / prevRate) * 100;
          overTenPct = Math.abs(variancePct) > 10;
        }
      }
      out.push({ product, month, rate, variancePct, overTenPct });
    }
  }
  out.sort((a, b) => {
    const c = a.product.localeCompare(b.product);
    if (c !== 0) return c;
    return monthSortKey(a.month) - monthSortKey(b.month);
  });
  return out;
}
